/**
 * NotesTimeline — Phase 2C CRM-style timestamped notes for a trainee.
 * Chronological (newest first), each entry keeps author + date + time.
 * Notes are additive: new remarks never overwrite older ones.
 */
import { useEffect, useState, useCallback } from 'react';
import { StickyNote, Pencil, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';

const ADMIN_ROLES = ['org_admin', 'admin', 'super_admin'];

function fmt(iso) {
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })} · ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

export default function NotesTimeline({ traineeId }) {
  const { get, post, put, del } = useApi();
  const { user, hasRole } = useAuth();
  const canWrite = hasRole('trainer');

  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // {note_id, text}

  const load = useCallback(() => {
    get(`/trainee-notes/${traineeId}`)
      .then(r => setNotes(r.notes || []))
      .catch(() => {});
  }, [get, traineeId]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await post(`/trainee-notes/${traineeId}`, { text: draft.trim() });
      setDraft('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add note'); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!editing?.text.trim()) return;
    try {
      await put(`/trainee-notes/note/${editing.note_id}`, { text: editing.text.trim() });
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Edit failed'); }
  };

  const remove = async (n) => {
    try {
      await del(`/trainee-notes/note/${n.note_id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const canModify = (n) => n.author_id === user?.user_id || ADMIN_ROLES.includes(user?.role);

  return (
    <Card data-testid="notes-timeline">
      <div className="p-3 border-b flex items-center gap-2">
        <StickyNote className="w-4 h-4 text-amber-600" />
        <h4 className="font-display text-sm font-semibold">Notes & Remarks</h4>
        <span className="text-xs text-muted-foreground ml-auto">{notes.length} note{notes.length === 1 ? '' : 's'}</span>
      </div>

      {canWrite && (
        <div className="p-3 border-b space-y-2">
          <Textarea rows={2} placeholder="Add a remark… (saved with your name, date & time)"
                    value={draft} onChange={e => setDraft(e.target.value)}
                    data-testid="note-input" />
          <div className="flex justify-end">
            <Button size="sm" onClick={addNote} disabled={saving || !draft.trim()}
                    data-testid="add-note-btn">
              {saving ? 'Saving…' : 'Add Note'}
            </Button>
          </div>
        </div>
      )}

      {notes.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">No notes yet. Remarks build a chronological history for this trainee.</p>
      ) : (
        <div className="relative p-4 space-y-4 max-h-72 overflow-y-auto" data-testid="notes-list">
          <div className="absolute left-[27px] top-6 bottom-6 w-px bg-border" />
          {notes.map(n => (
            <div key={n.note_id} className="relative flex gap-3" data-testid={`note-${n.note_id}`}>
              <div className="w-7 h-7 rounded-full bg-amber-500/15 text-amber-700 flex items-center justify-center text-[10px] font-semibold shrink-0 z-10 border border-amber-200">
                {(n.author_name || '?').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{n.author_name}</span>
                  {' '}· {fmt(n.created_at)}
                  {n.edited && <span className="italic"> · edited</span>}
                </p>
                {editing?.note_id === n.note_id ? (
                  <div className="mt-1 space-y-1">
                    <Textarea rows={2} value={editing.text}
                              onChange={e => setEditing(ed => ({ ...ed, text: e.target.value }))}
                              data-testid="edit-note-input" />
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}><X className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" onClick={saveEdit} data-testid="save-edit-note-btn"><Check className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm mt-0.5 whitespace-pre-wrap">{n.text}</p>
                )}
              </div>
              {canWrite && canModify(n) && editing?.note_id !== n.note_id && (
                <div className="flex gap-0.5 shrink-0">
                  <button className="p-1 text-muted-foreground hover:text-foreground"
                          onClick={() => setEditing({ note_id: n.note_id, text: n.text })}
                          data-testid={`edit-note-${n.note_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button className="p-1 text-muted-foreground hover:text-rose-600"
                          onClick={() => remove(n)}
                          data-testid={`delete-note-${n.note_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
